/**
 * ScamBlocker AI — Canlı Çağrılar Sayfası (/live)
 * Gerçek zamanlı çağrı akışı (SSE) — B2B organizasyon portalı.
 */

import { AlertTriangle, Phone, Radio, RefreshCw, Shield } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LiveMessage, LiveSocket } from '@/shared/lib/ws/liveSocket';
import styles from './LivePage.module.css';

// WS'in gönderdiği org_calls şekli (backend orgCallEvents/orgLiveStream).
interface LiveCall {
  id: string;
  caller_masked?: string;
  agent_id?: string;
  risk_score: number;
  risk_level: string;
  deepfake_score?: number | null;
  started_at?: string;
  status?: string;
}

const RISK_COLOR: Record<string, string> = {
  SAFE: 'var(--color-success)',
  CAUTION: 'var(--color-warning)',
  DANGEROUS: 'var(--color-danger)',
  DANGER: 'var(--color-danger)',
  CRITICAL: 'var(--color-critical)',
};

const RISK_LABEL_KEY: Record<string, string> = {
  SAFE: 'live.riskSafe',
  CAUTION: 'live.riskCaution',
  DANGEROUS: 'live.riskDanger',
  DANGER: 'live.riskDanger',
  CRITICAL: 'live.riskCritical',
};

function RiskBadge({ level, score }: { level: string; score: number }) {
  const { t } = useTranslation();
  const color = RISK_COLOR[level] ?? 'var(--color-muted)';
  const labelKey = RISK_LABEL_KEY[level];
  return (
    <span
      className={styles.riskBadge}
      style={{ color, borderColor: color, backgroundColor: `${color}18` }}
    >
      {score} · {labelKey ? t(labelKey) : level}
    </span>
  );
}

export function LivePage() {
  const { t } = useTranslation();
  const [calls, setCalls] = useState<LiveCall[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sockRef = useRef<LiveSocket | null>(null);

  // useCallback ZORUNLU: aşağıdaki effect `connect`'i bağımlılık alıyor.
  // Bunlar her render'da yeniden tanımlansaydı effect her render'da yeniden
  // koşar, WebSocket kapanıp yeniden açılırdı — üstelik gelen her mesaj
  // setCalls ile render tetiklediği için bağlantı kendi kendini döngüye
  // sokardı. setState fonksiyonları kararlı olduğundan bağımlılık listesi boş.
  const handleMessage = useCallback((msg: LiveMessage) => {
    if (msg.type === 'LIVE_CALLS' && Array.isArray(msg.calls)) {
      setCalls(msg.calls as LiveCall[]);
    } else if (msg.type === 'CALL_UPDATE' && msg.call) {
      const c = msg.call as LiveCall;
      setCalls((prev) => {
        const idx = prev.findIndex((x) => x.id === c.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...c };
          return next;
        }
        return [c, ...prev].slice(0, 50);
      });
    } else if (msg.type === 'CALL_ENDED' && msg.call) {
      const c = msg.call as LiveCall;
      setCalls((prev) => prev.filter((x) => x.id !== c.id));
    }
  }, []);

  const connect = useCallback(() => {
    setError(null);
    sockRef.current?.close();
    const sock = new LiveSocket({
      onMessage: handleMessage,
      onOpen: () => setConnected(true),
      onClose: () => {
        setConnected(false);
        setError('Bağlantı kesildi. Yeniden bağlanmaya çalışılıyor…');
      },
    });
    sockRef.current = sock;
    sock.connect();
  }, [handleMessage]);

  useEffect(() => {
    connect();
    return () => sockRef.current?.close();
  }, [connect]);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Radio size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('live.title')}</h1>
          <span className={`${styles.statusPill} ${connected ? styles.live : styles.offline}`}>
            <span className={styles.dot} />
            {connected ? t('live.statusLive') : t('live.statusOffline')}
          </span>
        </div>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={connect}
          title="Yeniden bağlan"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Boş durum */}
      {calls.length === 0 && !error && (
        <div className={styles.empty}>
          <Phone size={48} strokeWidth={1.2} />
          <p>{t('live.emptyTitle')}</p>
          <span>{t('live.emptyHint')}</span>
        </div>
      )}

      {/* Çağrı listesi */}
      {calls.length > 0 && (
        <div className={styles.callGrid}>
          {calls.map((call) => (
            <div key={call.id} className={styles.callCard}>
              <div className={styles.callHeader}>
                <Shield
                  size={18}
                  style={{ color: RISK_COLOR[call.risk_level] ?? 'var(--color-muted)' }}
                />
                <span className={styles.callerNum}>{call.caller_masked ?? 'Bilinmeyen'}</span>
                {call.agent_id && <span className={styles.seniorName}>→ {call.agent_id}</span>}
              </div>

              <div className={styles.callBody}>
                <RiskBadge level={call.risk_level} score={call.risk_score} />
                {call.deepfake_score != null && (
                  <span className={styles.scenario}>Deepfake: {call.deepfake_score}</span>
                )}
              </div>

              <div className={styles.callFooter}>
                <span className={styles.duration}>{call.status ?? 'active'}</span>
                <span className={styles.callTime}>
                  {call.started_at ? new Date(call.started_at).toLocaleTimeString('tr-TR') : '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
